import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Upload, ArrowRight, Sparkles, Shield, Download, Share2, CheckCircle2 } from 'lucide-react';
interface KnightingCeremonyProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (photoUrl: string) => void;
}
type Step = 'intro' | 'capture' | 'oath' | 'reveal' | 'share';
export const KnightingCeremony: React.FC<KnightingCeremonyProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [step, setStep] = useState<Step>('intro');
  const [gender, setGender] = useState<'male' | 'female' | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [captureMode, setCaptureMode] = useState<'choose' | 'camera'>('choose');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 1280
          },
          height: {
            ideal: 1280
          }
        },
        audio: false
      });
      streamRef.current = stream;
      setCaptureMode('camera');
      // Defer attaching to next tick so video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (err) {
      setCameraError('Could not access camera. Please allow camera permission or upload a photo instead.');
      setCaptureMode('choose');
    }
  };
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    // Mirror the image horizontally (selfie style)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setPhoto(dataUrl);
    stopCamera();
    setCaptureMode('choose');
  };
  const cancelCamera = () => {
    stopCamera();
    setCaptureMode('choose');
  };
  // Cleanup camera on unmount or modal close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setCaptureMode('choose');
    }
    return () => stopCamera();
  }, [isOpen]);
  if (!isOpen) return null;
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPhoto(url);
    }
  };
  const handleOathAccept = () => {
    setIsGenerating(true);
    // Simulate generation time
    setTimeout(() => {
      setIsGenerating(false);
      setStep('reveal');
    }, 3000);
  };
  // The CSS-built Card Frame
  const GeneratedCard = () => {
    const isFemale = gender === 'female';
    const floralColor = isFemale ? 'from-[#8B0000]/20 to-[#D4AF37]/20' : 'from-[#0B0F19] to-[#131B2B]';
    const floralAccent = isFemale ? 'text-[#8B0000]' : 'text-[#131B2B]';
    return <div className="relative w-full max-w-md mx-auto aspect-[4/5] bg-[#0B0F19] border-[6px] border-[#131B2B] shadow-2xl overflow-hidden flex flex-col" data-id="element-1480">
        {/* Outer Gold Border */}
        <div className="absolute inset-0 border-[3px] border-[#D4AF37] m-1 pointer-events-none z-20" data-id="element-1481" />
        <div className="absolute inset-0 border border-[#D4AF37]/50 m-2 pointer-events-none z-20" data-id="element-1482" />

        {/* Header */}
        <div className="relative z-20 pt-4 pb-2 text-center bg-[#0B0F19]" data-id="element-1483">
          <div className="flex justify-center mb-1" data-id="element-1484">
            <svg className="w-6 h-6 text-[#D4AF37]" viewBox="0 0 24 24" fill="currentColor" data-id="element-1485">
              <path d="M12 2L15 8H9L12 2Z" data-id="element-1486" />
              <path d="M6 10H18V22H6V10Z" data-id="element-1487" />
              <path d="M12 12C13.1046 12 14 12.8954 14 14C14 15.1046 13.1046 16 12 16C10.8954 16 10 15.1046 10 14C10 12.8954 10.8954 12 12 12Z" fill="#0B0F19" data-id="element-1488" />
            </svg>
          </div>
          <h3 className="text-[#D4AF37] font-serif text-sm tracking-[0.2em] uppercase" data-id="element-1489">
            Lahore Treasury
          </h3>
          <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.3em] uppercase mt-0.5" data-id="element-1490">
            Enter the Lahore Court
          </p>
        </div>

        {/* Main Content Area with Floral Side Panels */}
        <div className="relative flex-1 flex mx-3 mb-3 border-2 border-[#D4AF37] bg-[#0B0F19]" data-id="element-1491">
          {/* Left Floral Panel */}
          <div className={`w-12 border-r-2 border-[#D4AF37] bg-gradient-to-b ${floralColor} flex flex-col items-center py-4 gap-4 overflow-hidden`} data-id="element-1492">
            {[...Array(5)].map((_, i) => <svg key={`l-${i}`} className={`w-6 h-6 ${floralAccent} opacity-50`} viewBox="0 0 24 24" fill="currentColor" data-id="element-1493">
                <path d="M12 2C12 2 15 7 15 12C15 17 12 22 12 22C12 22 9 17 9 12C9 7 12 2 12 2Z" data-id="element-1494" />
                <path d="M2 12C2 12 7 9 12 9C17 9 22 12 22 12C22 12 17 15 12 15C7 15 2 12 2 12Z" data-id="element-1495" />
              </svg>)}
          </div>

          {/* Center Portrait Area with Cusped Arch */}
          <div className="relative flex-1 bg-[#131B2B] overflow-hidden" data-id="element-1496">
            {/* The Photo */}
            {photo && <img src={photo} alt="Knighted Portrait" className="absolute inset-0 w-full h-full object-cover" data-id="element-1497" />}

            {/* Cusped Arch SVG Overlay */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-1498">
              <path d="M0,0 L100,0 L100,100 L0,100 Z M0,100 L0,30 C10,30 15,25 20,20 C25,15 30,10 50,10 C70,10 75,15 80,20 C85,25 90,30 100,30 L100,100 Z" fill="#0B0F19" data-id="element-1499" />
              <path d="M0,30 C10,30 15,25 20,20 C25,15 30,10 50,10 C70,10 75,15 80,20 C85,25 90,30 100,30" fill="none" stroke="#D4AF37" strokeWidth="1.5" data-id="element-1500" />
            </svg>

            {/* Name Plate */}
            <div className="absolute bottom-0 inset-x-0 bg-[#0B0F19] border-t-2 border-[#D4AF37] p-4 text-center z-30" data-id="element-1501">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1502">
                {isFemale ? 'Sardarni of the Court' : 'Sardar of the Court'}
              </p>
              <h2 className="font-serif text-2xl text-[#D4AF37] mb-2 uppercase tracking-wide" data-id="element-1503">
                Harnoor Singh
              </h2>
              <p className="text-[#D4AF37]/80 text-[10px] font-serif italic" data-id="element-1504">
                A newly appointed noble of the Lahore Darbar, sworn to protect
                the empire.
              </p>
            </div>
          </div>

          {/* Right Floral Panel */}
          <div className={`w-12 border-l-2 border-[#D4AF37] bg-gradient-to-b ${floralColor} flex flex-col items-center py-4 gap-4 overflow-hidden`} data-id="element-1505">
            {[...Array(5)].map((_, i) => <svg key={`r-${i}`} className={`w-6 h-6 ${floralAccent} opacity-50`} viewBox="0 0 24 24" fill="currentColor" data-id="element-1506">
                <path d="M12 2C12 2 15 7 15 12C15 17 12 22 12 22C12 22 9 17 9 12C9 7 12 2 12 2Z" data-id="element-1507" />
                <path d="M2 12C2 12 7 9 12 9C17 9 22 12 22 12C22 12 17 15 12 15C7 15 2 12 2 12Z" data-id="element-1508" />
              </svg>)}
          </div>
        </div>

        {/* Founders Edition Badge */}
        <div className="absolute -bottom-2 -left-2 w-28 h-28 bg-[#0B0F19] rounded-full border-4 border-[#131B2B] z-40 flex items-center justify-center shadow-xl" data-id="element-1509">
          <div className="w-[88%] h-[88%] rounded-full border border-[#D4AF37] border-dashed flex flex-col items-center justify-center p-2 text-center" data-id="element-1510">
            <svg className="w-5 h-5 text-[#D4AF37] mb-1" viewBox="0 0 24 24" fill="currentColor" data-id="element-1511">
              <path d="M2 20H22V22H2V20ZM4 12L7 18H17L20 12L16 14L12 8L8 14L4 12Z" data-id="element-1512" />
            </svg>
            <span className="text-[#D4AF37] text-[9px] font-serif tracking-widest leading-tight" data-id="element-1513">
              FOUNDERS'
              <br data-id="element-1514" />
              EDITION
            </span>
          </div>
        </div>
      </div>;
  };
  return <AnimatePresence data-id="element-1515">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-1516">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-[#070A12]/90 backdrop-blur-md" onClick={onClose} data-id="element-1517" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-[#0B0F19] border border-[#D4AF37]/30 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1518">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#D4AF37]/15" data-id="element-1519">
            <div className="flex items-center gap-3" data-id="element-1520">
              <Shield className="w-5 h-5 text-[#D4AF37]" data-id="element-1521" />
              <h2 className="font-serif text-xl text-[#FDFBF7]" data-id="element-1522">
                Knighting Ceremony
              </h2>
            </div>
            <button onClick={onClose} className="p-2 text-[#FDFBF7]/50 hover:text-[#FDFBF7] transition-colors" data-id="element-1523">
              <X className="w-5 h-5" data-id="element-1524" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-10" data-id="element-1525">
            {step === 'intro' && <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-1526">
                <div className="space-y-4" data-id="element-1527">
                  <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7]" data-id="element-1528">
                    The Knighting of the Court
                  </h1>
                  <p className="text-[#FDFBF7]/60 text-lg max-w-xl mx-auto" data-id="element-1529">
                    An optional rite. A permanent honour. Present yourself to
                    the Darbar and receive your official Founders' Edition
                    portrait.
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-bold tracking-widest uppercase mt-4" data-id="element-1530">
                    <Sparkles className="w-4 h-4" data-id="element-1531" />
                    Reward: +2,500 XP
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-8" data-id="element-1532">
                  <div className="w-48 aspect-[4/5] bg-[#131B2B] border border-[#D4AF37]/30 flex flex-col items-center justify-center p-4" data-id="element-1533">
                    <div className="w-24 h-24 rounded-full bg-[#0B0F19] border-2 border-[#D4AF37]/20 flex items-center justify-center mb-4" data-id="element-1534">
                      <Camera className="w-8 h-8 text-[#D4AF37]/40" data-id="element-1535" />
                    </div>
                    <p className="text-[#FDFBF7]/40 text-xs uppercase tracking-widest" data-id="element-1536">
                      Your Photo
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-2 text-[#D4AF37]" data-id="element-1537">
                    <ArrowRight className="w-6 h-6" data-id="element-1538" />
                    <span className="text-[10px] uppercase tracking-widest" data-id="element-1539">
                      Transforms Into
                    </span>
                  </div>

                  <div className="w-48 aspect-[4/5] border border-[#D4AF37]/30 relative overflow-hidden" data-id="element-1540">
                    <img src="https://cdn.magicpatterns.com/uploads/mYwWxceeL8ryPc8ZuV43iM/pasted-image.png" alt="Example" className="w-full h-full object-cover" data-id="element-1541" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6" data-id="element-1542">
                  <button onClick={() => setStep('capture')} className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors" data-id="element-1543">
                    Begin Ceremony
                  </button>
                  <button onClick={onClose} className="w-full sm:w-auto px-8 py-4 border border-[#D4AF37]/30 text-[#FDFBF7] font-bold uppercase tracking-widest text-sm hover:bg-[#131B2B] transition-colors" data-id="element-1544">
                    Maybe Later
                  </button>
                </div>
              </div>}

            {step === 'capture' && <div className="max-w-xl mx-auto space-y-8" data-id="element-1545">
                <div className="text-center space-y-2" data-id="element-1546">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-1547">
                    Present Yourself
                  </h2>
                  <p className="text-[#FDFBF7]/60" data-id="element-1548">
                    Select your title and provide a clear portrait.
                  </p>
                </div>

                <div className="space-y-4" data-id="element-1549">
                  <label className="text-[#D4AF37] text-xs uppercase tracking-widest" data-id="element-1550">
                    1. Your Title
                  </label>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1551">
                    <button onClick={() => setGender('male')} className={`p-4 border ${gender === 'male' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/30 bg-[#131B2B]'} transition-colors text-center`} data-id="element-1552">
                      <span className="block font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-1553">
                        Sardar
                      </span>
                      <span className="text-[#FDFBF7]/50 text-xs" data-id="element-1554">(His)</span>
                    </button>
                    <button onClick={() => setGender('female')} className={`p-4 border ${gender === 'female' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/30 bg-[#131B2B]'} transition-colors text-center`} data-id="element-1555">
                      <span className="block font-serif text-xl text-[#FDFBF7] mb-1" data-id="element-1556">
                        Sardarni
                      </span>
                      <span className="text-[#FDFBF7]/50 text-xs" data-id="element-1557">(Her)</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1558">
                  <label className="text-[#D4AF37] text-xs uppercase tracking-widest" data-id="element-1559">
                    2. Your Portrait
                  </label>
                  <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handlePhotoUpload} data-id="element-1560" />

                  {/* Captured / uploaded preview */}
                  {photo && captureMode === 'choose' && <div className="relative w-full aspect-video border border-[#D4AF37]/30 bg-[#131B2B] overflow-hidden" data-id="element-1561">
                      <img src={photo} alt="Preview" className="w-full h-full object-cover" data-id="element-1562" />
                      <button onClick={() => setPhoto(null)} className="absolute top-4 right-4 p-2 bg-[#0B0F19]/80 backdrop-blur text-[#FDFBF7] hover:text-red-400 rounded-full" data-id="element-1563">
                        <X className="w-4 h-4" data-id="element-1564" />
                      </button>
                      <div className="absolute bottom-3 left-3 px-2 py-1 bg-[#0B0F19]/80 backdrop-blur text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-1565">
                        Ready
                      </div>
                    </div>}

                  {/* Live camera */}
                  {captureMode === 'camera' && <div className="relative w-full aspect-video border border-[#D4AF37] bg-[#131B2B] overflow-hidden" data-id="element-1566">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" data-id="element-1567" />
                      {/* Center framing guide */}
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-id="element-1568">
                        <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full border-2 border-[#D4AF37]/60 shadow-[0_0_0_9999px_rgba(11,15,25,0.45)]" data-id="element-1569" />
                      </div>
                      <div className="absolute top-3 left-3 px-2 py-1 bg-[#0B0F19]/80 backdrop-blur text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-1570">
                        Live · Frame your face
                      </div>
                      <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3" data-id="element-1571">
                        <button onClick={cancelCamera} className="px-4 py-2 bg-[#0B0F19]/80 backdrop-blur border border-[#FDFBF7]/30 text-[#FDFBF7] text-[10px] uppercase tracking-widest hover:bg-[#131B2B] transition-colors" data-id="element-1572">
                          Cancel
                        </button>
                        <button onClick={capturePhoto} className="px-6 py-3 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2 shadow-xl" data-id="element-1573">
                          <Camera className="w-4 h-4" data-id="element-1574" /> Capture
                        </button>
                      </div>
                    </div>}

                  {/* Choose method */}
                  {!photo && captureMode === 'choose' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1575">
                      <button onClick={startCamera} className="aspect-video border-2 border-dashed border-[#D4AF37]/30 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-colors flex flex-col items-center justify-center gap-3 group" data-id="element-1576">
                        <div className="w-14 h-14 rounded-full bg-[#0B0F19] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1577">
                          <Camera className="w-6 h-6 text-[#D4AF37]" data-id="element-1578" />
                        </div>
                        <div className="text-center" data-id="element-1579">
                          <p className="text-[#FDFBF7] font-serif text-base" data-id="element-1580">
                            Use Camera
                          </p>
                          <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5" data-id="element-1581">
                            Take a live portrait
                          </p>
                        </div>
                      </button>
                      <button onClick={() => fileInputRef.current?.click()} className="aspect-video border-2 border-dashed border-[#D4AF37]/30 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-colors flex flex-col items-center justify-center gap-3 group" data-id="element-1582">
                        <div className="w-14 h-14 rounded-full bg-[#0B0F19] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1583">
                          <Upload className="w-6 h-6 text-[#D4AF37]" data-id="element-1584" />
                        </div>
                        <div className="text-center" data-id="element-1585">
                          <p className="text-[#FDFBF7] font-serif text-base" data-id="element-1586">
                            Upload Photo
                          </p>
                          <p className="text-[#FDFBF7]/50 text-[10px] mt-0.5" data-id="element-1587">
                            Clear face, good lighting
                          </p>
                        </div>
                      </button>
                    </div>}

                  {cameraError && <p className="text-[#8B0000] text-xs text-center" data-id="element-1588">
                      {cameraError}
                    </p>}

                  {/* Retake controls when photo exists */}
                  {photo && captureMode === 'choose' && <div className="flex items-center justify-center gap-3 pt-2" data-id="element-1589">
                      <button onClick={() => {
                  setPhoto(null);
                  startCamera();
                }} className="text-[#D4AF37] text-[10px] uppercase tracking-widest hover:underline flex items-center gap-1.5" data-id="element-1590">
                        <Camera className="w-3 h-3" data-id="element-1591" /> Retake with Camera
                      </button>
                      <span className="text-[#FDFBF7]/20" data-id="element-1592">·</span>
                      <button onClick={() => {
                  setPhoto(null);
                  fileInputRef.current?.click();
                }} className="text-[#D4AF37] text-[10px] uppercase tracking-widest hover:underline flex items-center gap-1.5" data-id="element-1593">
                        <Upload className="w-3 h-3" data-id="element-1594" /> Upload Different
                      </button>
                    </div>}
                </div>

                <div className="pt-6" data-id="element-1595">
                  <button disabled={!gender || !photo} onClick={() => setStep('oath')} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1596">
                    Proceed to Oath
                  </button>
                </div>
              </div>}

            {step === 'oath' && <div className="max-w-2xl mx-auto text-center space-y-12 py-10" data-id="element-1597">
                {isGenerating ? <div className="space-y-8" data-id="element-1598">
                    <div className="relative w-32 h-32 mx-auto" data-id="element-1599">
                      <div className="absolute inset-0 border-4 border-[#D4AF37]/20 rounded-full" data-id="element-1600" />
                      <div className="absolute inset-0 border-4 border-[#D4AF37] rounded-full border-t-transparent animate-spin" data-id="element-1601" />
                      <Shield className="absolute inset-0 m-auto w-10 h-10 text-[#D4AF37] animate-pulse" data-id="element-1602" />
                    </div>
                    <div className="space-y-2" data-id="element-1603">
                      <h2 className="font-serif text-2xl text-[#D4AF37]" data-id="element-1604">
                        Forging Your Legacy...
                      </h2>
                      <p className="text-[#FDFBF7]/60" data-id="element-1605">
                        The royal painters are completing your portrait.
                      </p>
                    </div>
                  </div> : <>
                    <Shield className="w-16 h-16 text-[#D4AF37] mx-auto opacity-50" data-id="element-1606" />
                    <div className="space-y-6" data-id="element-1607">
                      <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-relaxed" data-id="element-1608">
                        "I pledge my loyalty to the Lahore Darbar. I shall
                        uphold the honour of the empire, protect its treasures,
                        and stand as a guardian of its legacy."
                      </h2>
                    </div>
                    <button onClick={handleOathAccept} className="px-10 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors" data-id="element-1609">
                      I Accept This Oath
                    </button>
                  </>}
              </div>}

            {step === 'reveal' && <div className="max-w-4xl mx-auto flex flex-col lg:flex-row items-center gap-10" data-id="element-1610">
                <div className="flex-1 w-full" data-id="element-1611">
                  <GeneratedCard data-id="element-1612" />
                </div>
                <div className="flex-1 space-y-8 text-center lg:text-left" data-id="element-1613">
                  <div className="space-y-4" data-id="element-1614">
                    <div className="inline-flex items-center gap-2 text-[#D4AF37] mb-2" data-id="element-1615">
                      <CheckCircle2 className="w-5 h-5" data-id="element-1616" />
                      <span className="text-sm font-bold tracking-widest uppercase" data-id="element-1617">
                        Knighting Complete
                      </span>
                    </div>
                    <h2 className="font-serif text-4xl text-[#FDFBF7]" data-id="element-1618">
                      Arise, Noble of the Court
                    </h2>
                    <p className="text-[#FDFBF7]/60 text-lg" data-id="element-1619">
                      Your portrait has been added to the royal archives. You
                      have been awarded 2,500 XP for completing this rite.
                    </p>
                  </div>

                  <div className="flex flex-col gap-4" data-id="element-1620">
                    <button onClick={() => setStep('share')} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-1621">
                      <Download className="w-4 h-4" data-id="element-1622" /> Save & Share Portrait
                    </button>
                    <button onClick={() => {
                  onComplete(photo!);
                  onClose();
                }} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] font-bold uppercase tracking-widest text-sm hover:bg-[#131B2B] transition-colors" data-id="element-1623">
                      Return to Dashboard
                    </button>
                  </div>
                </div>
              </div>}

            {step === 'share' && <div className="max-w-md mx-auto space-y-8 text-center" data-id="element-1624">
                <GeneratedCard data-id="element-1625" />
                <div className="grid grid-cols-2 gap-4" data-id="element-1626">
                  <button className="p-4 border border-[#D4AF37]/30 bg-[#131B2B] hover:bg-[#D4AF37]/10 transition-colors flex flex-col items-center gap-2 text-[#FDFBF7]" data-id="element-1627">
                    <Download className="w-5 h-5 text-[#D4AF37]" data-id="element-1628" />
                    <span className="text-xs uppercase tracking-widest" data-id="element-1629">
                      Download
                    </span>
                  </button>
                  <button className="p-4 border border-[#D4AF37]/30 bg-[#131B2B] hover:bg-[#D4AF37]/10 transition-colors flex flex-col items-center gap-2 text-[#FDFBF7]" data-id="element-1630">
                    <Share2 className="w-5 h-5 text-[#D4AF37]" data-id="element-1631" />
                    <span className="text-xs uppercase tracking-widest" data-id="element-1632">
                      Share
                    </span>
                  </button>
                </div>
                <button onClick={() => {
              onComplete(photo!);
              onClose();
            }} className="text-[#D4AF37] text-sm uppercase tracking-widest hover:underline" data-id="element-1633">
                  Back to Dashboard
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
};